/**
 * /fleet/:id — a vehicle's detail page: the fleet-management hub.
 *
 * Profile + availability status, a photo gallery, the service log (maintenance
 * history), and issue reports crew flagged from the field. Supervisor/admin only
 * (the route is role-gated); crew report issues from their task screen.
 */
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  Check,
  FileText,
  ImagePlus,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Truck,
  Wrench,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import type {
  ActivityMedia,
  Attachment,
  VehicleIssue,
  VehicleIssueSeverity,
  VehicleServiceCategory,
  VehicleServiceLog,
  VehicleStatus,
  VehicleType,
} from '@vari-crew/schemas';
import {
  uploadIssuePhoto,
  uploadServiceLogPhoto,
  uploadVehiclePhoto,
  useCreateServiceLog,
  useDeleteServiceLog,
  useUpdateVehicle,
  useUpdateVehicleIssue,
  useVehicle,
  useVehicleIssues,
  useVehiclePhotos,
  useVehicleServiceLogs,
} from '@/api/vehicles';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { MediaGallery } from '@/components/MediaGallery';
import { ConfirmDialog } from '@/components/ui/Dialog';
import { formatDate, formatMoneyCents, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useQueryClient } from '@tanstack/react-query';
import { STATUS_META, VEHICLE_TYPES, typeLabel } from './Fleet';

const SERVICE_CATEGORIES: { value: VehicleServiceCategory; label: string }[] = [
  { value: 'oil_change', label: 'Oil change' },
  { value: 'tires', label: 'Tires' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'repair', label: 'Repair' },
  { value: 'registration', label: 'Registration' },
  { value: 'other', label: 'Other' },
];
const categoryLabel = (c: VehicleServiceCategory) =>
  SERVICE_CATEGORIES.find((x) => x.value === c)?.label ?? c;

const SEVERITY_META: Record<VehicleIssueSeverity, { label: string; cls: string }> = {
  low: { label: 'Low', cls: 'text-ink-faint border-line' },
  normal: { label: 'Normal', cls: 'text-status-enroute-fg border-status-enroute' },
  high: { label: 'High', cls: 'text-status-blocked-fg border-status-blocked' },
};

const selectCls = 'h-9 rounded border border-line bg-bg px-2 text-sm';

/** Attachment → the gallery's media shape. */
const toMedia = (a: Attachment): ActivityMedia => ({
  id: a.id,
  kind: a.kind === 'video' ? 'video' : 'photo',
  mime_type: a.mime_type,
  file_url: a.file_url,
});

export function FleetVehicle() {
  const { id } = useParams<{ id: string }>();
  const { data: vehicle, isLoading, error } = useVehicle(id);

  if (isLoading) {
    return (
      <div className="p-6 grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !vehicle || !id) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <Link to="/fleet" className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink">
          <ArrowLeft className="w-4 h-4" /> Fleet
        </Link>
        <div className="text-sm text-status-blocked-fg">
          {(error as Error)?.message ?? 'Vehicle not found.'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <Link to="/fleet" className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink">
        <ArrowLeft className="w-4 h-4" /> Fleet
      </Link>

      <ProfileCard vehicleId={id} />
      <PhotosCard vehicleId={id} />
      <ServiceLogCard vehicleId={id} />
      <IssuesCard vehicleId={id} />
    </div>
  );
}

// ---- Profile + status -------------------------------------------------------
function ProfileCard({ vehicleId }: { vehicleId: string }) {
  const { data: v } = useVehicle(vehicleId);
  const update = useUpdateVehicle();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    type: 'cargo_van' as VehicleType,
    make: '',
    model: '',
    year: '',
    plate: '',
    vin: '',
    registration_expires_on: '',
    notes: '',
  });

  if (!v) return null;

  function startEdit() {
    setForm({
      type: v!.type,
      make: v!.make ?? '',
      model: v!.model ?? '',
      year: v!.year ? String(v!.year) : '',
      plate: v!.plate ?? '',
      vin: v!.vin ?? '',
      registration_expires_on: v!.registration_expires_on ?? '',
      notes: v!.notes ?? '',
    });
    setEditing(true);
  }

  async function saveProfile() {
    try {
      await update.mutateAsync({
        id: vehicleId,
        body: {
          type: form.type,
          make: form.make.trim() || null,
          model: form.model.trim() || null,
          year: form.year ? Number(form.year) : null,
          plate: form.plate.trim() || null,
          vin: form.vin.trim() || null,
          registration_expires_on: form.registration_expires_on || null,
          notes: form.notes.trim() || null,
        },
      });
      toast.success('Vehicle updated');
      setEditing(false);
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    }
  }

  async function setStatus(status: VehicleStatus) {
    try {
      await update.mutateAsync({ id: vehicleId, body: { status } });
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    }
  }

  async function saveStatusNote(note: string) {
    await update.mutateAsync({ id: vehicleId, body: { status_note: note.trim() || null } });
  }

  const profileLine = [typeLabel(v.type), [v.year, v.make, v.model].filter(Boolean).join(' '), v.plate]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-lg bg-fill-soft grid place-items-center shrink-0">
              <Truck className="w-6 h-6 text-ink-dim" />
            </div>
            <div className="min-w-0">
              <CardTitle className="font-mono text-xl">{v.label}</CardTitle>
              <div className="text-sm text-ink-dim truncate">{profileLine || 'No details yet'}</div>
            </div>
          </div>
          {!editing && (
            <Button variant="outline" size="sm" onClick={startEdit}>
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status control */}
        <div>
          <div className="text-xs font-medium text-ink-faint mb-1">Status</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {(['active', 'out_of_service', 'retired'] as VehicleStatus[]).map((s) => (
              <button
                key={s}
                onClick={() => void setStatus(s)}
                disabled={update.isPending}
                className={cn(
                  'text-xs px-2.5 py-1 rounded-full border transition-colors',
                  v.status === s
                    ? STATUS_META[s].cls
                    : 'border-line text-ink-dim hover:bg-fill-soft',
                )}
              >
                {STATUS_META[s].label}
              </button>
            ))}
          </div>
          {v.status === 'out_of_service' && (
            <StatusNote initial={v.status_note ?? ''} onSave={saveStatusNote} />
          )}
        </div>

        {editing ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-ink-faint">Type</span>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as VehicleType }))}
                className={cn('mt-1 w-full', selectCls)}
              >
                {VEHICLE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <Field label="Year">
              <Input
                type="number"
                value={form.year}
                onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
                placeholder="2022"
                className="mt-1 h-9"
              />
            </Field>
            <Field label="Make">
              <Input
                value={form.make}
                onChange={(e) => setForm((f) => ({ ...f, make: e.target.value }))}
                placeholder="Ford"
                className="mt-1 h-9"
              />
            </Field>
            <Field label="Model">
              <Input
                value={form.model}
                onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
                placeholder="Transit 250"
                className="mt-1 h-9"
              />
            </Field>
            <Field label="Plate">
              <Input
                value={form.plate}
                onChange={(e) => setForm((f) => ({ ...f, plate: e.target.value }))}
                placeholder="TX-0000"
                className="mt-1 h-9"
              />
            </Field>
            <Field label="Registration expires">
              <Input
                type="date"
                value={form.registration_expires_on}
                onChange={(e) =>
                  setForm((f) => ({ ...f, registration_expires_on: e.target.value }))
                }
                className="mt-1 h-9"
              />
            </Field>
            <label className="block col-span-2">
              <span className="text-xs font-medium text-ink-faint">VIN</span>
              <Input
                value={form.vin}
                onChange={(e) => setForm((f) => ({ ...f, vin: e.target.value }))}
                placeholder="1FTBW2CM3NKA12345"
                className="mt-1 h-9 font-mono"
              />
            </label>
            <label className="block col-span-2">
              <span className="text-xs font-medium text-ink-faint">Notes</span>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Anything worth knowing about this truck…"
                className="mt-1"
                rows={2}
              />
            </label>
            <div className="col-span-2 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={update.isPending}>
                Cancel
              </Button>
              <Button size="sm" onClick={saveProfile} disabled={update.isPending}>
                {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {(v.vin || v.registration_expires_on) && (
              <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-sm">
                {v.vin && (
                  <>
                    <dt className="text-ink-faint">VIN</dt>
                    <dd className="font-mono break-all">{v.vin}</dd>
                  </>
                )}
                {v.registration_expires_on && (
                  <>
                    <dt className="text-ink-faint">Registration</dt>
                    <dd>expires {formatDate(v.registration_expires_on)}</dd>
                  </>
                )}
              </dl>
            )}
            {v.notes && <p className="text-sm text-ink-dim whitespace-pre-wrap">{v.notes}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusNote({ initial, onSave }: { initial: string; onSave: (s: string) => Promise<void> }) {
  const [val, setVal] = useState(initial);
  return (
    <div className="mt-2 flex items-center gap-2">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => val !== initial && void onSave(val)}
        placeholder="Why is it out of service? (e.g. brakes — back Thursday)"
        className="h-8 text-sm"
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-faint">{label}</span>
      {children}
    </label>
  );
}

// ---- Photos -----------------------------------------------------------------
function PhotosCard({ vehicleId }: { vehicleId: string }) {
  const { data } = useVehiclePhotos(vehicleId);
  const photos = data?.data ?? [];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Camera className="w-4 h-4 text-ink-dim" /> Photos
        </CardTitle>
        <AddPhotosButton
          label="Add photos"
          upload={(file) => uploadVehiclePhoto(vehicleId, file)}
          invalidateKey={['vehicle-photos', vehicleId]}
        />
      </CardHeader>
      <CardContent>
        {photos.length === 0 ? (
          <p className="text-sm text-ink-faint">
            No photos yet — add shots of the vehicle, damage, or its condition.
          </p>
        ) : (
          <MediaGallery media={photos.map(toMedia)} />
        )}
      </CardContent>
    </Card>
  );
}

function AddPhotosButton({
  label,
  upload,
  invalidateKey,
  variant = 'outline',
}: {
  label: string;
  upload: (file: File) => Promise<unknown>;
  invalidateKey: unknown[];
  variant?: 'outline' | 'ghost';
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    let failed = 0;
    for (const file of Array.from(files)) {
      try {
        await upload(file);
      } catch {
        failed += 1;
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    void qc.invalidateQueries({ queryKey: invalidateKey });
    if (failed > 0) toast.error(`${failed} photo(s) failed to upload.`);
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        capture="environment"
        className="hidden"
        onChange={(e) => void onFiles(e.target.files)}
      />
      <Button
        variant={variant}
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
        {label}
      </Button>
    </>
  );
}

// ---- Service log ------------------------------------------------------------
function ServiceLogCard({ vehicleId }: { vehicleId: string }) {
  const { data } = useVehicleServiceLogs(vehicleId);
  const del = useDeleteServiceLog(vehicleId);
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const logs = data?.data ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Wrench className="w-4 h-4 text-ink-dim" /> Service log
        </CardTitle>
        <Button variant="outline" size="sm" onClick={() => setAdding((v) => !v)}>
          <Plus className="w-3.5 h-3.5" /> Add service
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && <AddServiceForm vehicleId={vehicleId} onDone={() => setAdding(false)} />}
        {logs.length === 0 && !adding ? (
          <p className="text-sm text-ink-faint">No service recorded yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {logs.map((log) => (
              <ServiceLogRow key={log.id} log={log} onDelete={() => setConfirmDel(log.id)} />
            ))}
          </ul>
        )}
      </CardContent>
      <ConfirmDialog
        open={confirmDel !== null}
        onClose={() => setConfirmDel(null)}
        onConfirm={() =>
          confirmDel &&
          del.mutate(confirmDel, {
            onSuccess: () => {
              setConfirmDel(null);
              toast.success('Service entry removed');
            },
            onError: (e) => toast.error(`Failed: ${e.message}`),
          })
        }
        title="Remove this service entry?"
        body="This deletes the entry and its receipt photos. This can't be undone."
        confirmLabel="Remove"
        tone="danger"
        busy={del.isPending}
      />
    </Card>
  );
}

function ServiceLogRow({ log, onDelete }: { log: VehicleServiceLog; onDelete: () => void }) {
  return (
    <li className="py-3 first:pt-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{categoryLabel(log.category)}</span>
            <span className="text-xs text-ink-faint">{formatDate(log.performed_on)}</span>
            {log.cost_cents != null && (
              <span className="text-xs font-mono text-ink-dim">{formatMoneyCents(log.cost_cents)}</span>
            )}
          </div>
          <div className="text-xs text-ink-dim mt-0.5 flex flex-wrap gap-x-2">
            {log.vendor && <span>{log.vendor}</span>}
            {log.odometer != null && <span>· {log.odometer.toLocaleString()} mi</span>}
            {log.created_by_name && <span>· logged by {log.created_by_name}</span>}
          </div>
          {log.notes && <p className="text-sm text-ink-dim mt-1 whitespace-pre-wrap">{log.notes}</p>}
          {log.photos.length > 0 &&
            (() => {
              // Receipts can be images or PDFs (§E). Images/videos ride the
              // gallery; PDFs (and any non-media) render as a download link.
              const isMedia = (p: Attachment) =>
                p.mime_type.startsWith('image/') || p.mime_type.startsWith('video/');
              const media = log.photos.filter(isMedia);
              const docs = log.photos.filter((p) => !isMedia(p));
              return (
                <div className="mt-2 space-y-1.5">
                  {media.length > 0 && <MediaGallery media={media.map(toMedia)} size="sm" />}
                  {docs.map((d) => (
                    <a
                      key={d.id}
                      href={d.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      {d.original_filename || 'Receipt.pdf'}
                    </a>
                  ))}
                </div>
              );
            })()}
        </div>
        <button
          onClick={onDelete}
          className="text-ink-faint hover:text-status-blocked-fg shrink-0"
          aria-label="Remove service entry"
          title="Remove"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </li>
  );
}

function AddServiceForm({ vehicleId, onDone }: { vehicleId: string; onDone: () => void }) {
  const create = useCreateServiceLog(vehicleId);
  const qc = useQueryClient();
  const [performedOn, setPerformedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState<VehicleServiceCategory>('oil_change');
  const [vendor, setVendor] = useState('');
  const [cost, setCost] = useState('');
  const [odometer, setOdometer] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  async function submit() {
    if (!performedOn) {
      toast.error('Pick the service date.');
      return;
    }
    try {
      const created = await create.mutateAsync({
        performed_on: performedOn,
        category,
        vendor: vendor.trim() || null,
        cost_cents: cost ? Math.round(parseFloat(cost) * 100) : null,
        odometer: odometer ? Number(odometer) : null,
        notes: notes.trim() || null,
      });
      for (const f of files) {
        try {
          await uploadServiceLogPhoto(created.id, f);
        } catch {
          /* best-effort receipt photo */
        }
      }
      void qc.invalidateQueries({ queryKey: ['vehicle-service-logs', vehicleId] });
      toast.success('Service logged');
      onDone();
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    }
  }

  return (
    <div className="rounded-lg border border-line p-3 space-y-3 bg-fill-soft/40">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date">
          <Input type="date" value={performedOn} onChange={(e) => setPerformedOn(e.target.value)} className="mt-1 h-9" />
        </Field>
        <label className="block">
          <span className="text-xs font-medium text-ink-faint">What</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as VehicleServiceCategory)}
            className={cn('mt-1 w-full', selectCls)}
          >
            {SERVICE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <Field label="Vendor">
          <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="In-house / shop name" className="mt-1 h-9" />
        </Field>
        <Field label="Cost ($)">
          <Input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" className="mt-1 h-9" />
        </Field>
        <Field label="Odometer (mi)">
          <Input type="number" value={odometer} onChange={(e) => setOdometer(e.target.value)} placeholder="52340" className="mt-1 h-9" />
        </Field>
      </div>
      <label className="block">
        <span className="text-xs font-medium text-ink-faint">Notes</span>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" placeholder="Parts, observations…" />
      </label>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
      />
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
          <ImagePlus className="w-3.5 h-3.5" />
          {files.length > 0 ? `${files.length} receipt file(s)` : 'Attach receipt (photo or PDF)'}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onDone} disabled={create.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={create.isPending}>
            {create.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Issues -----------------------------------------------------------------
function IssuesCard({ vehicleId }: { vehicleId: string }) {
  const { data } = useVehicleIssues(vehicleId);
  const issues = data?.data ?? [];
  const open = issues.filter((i) => i.status === 'open');
  const resolved = issues.filter((i) => i.status === 'resolved');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-ink-dim" /> Issues
          {open.length > 0 && (
            <span className="text-[11px] px-1.5 py-0.5 rounded-full border border-status-blocked text-status-blocked-fg bg-status-blocked/20 font-medium">
              {open.length} open
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {issues.length === 0 ? (
          <p className="text-sm text-ink-faint">
            No issues reported. Crew can flag a problem from their task screen.
          </p>
        ) : (
          <ul className="space-y-3">
            {[...open, ...resolved].map((issue) => (
              <IssueRow key={issue.id} vehicleId={vehicleId} issue={issue} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function IssueRow({ vehicleId, issue }: { vehicleId: string; issue: VehicleIssue }) {
  const update = useUpdateVehicleIssue(vehicleId);
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState('');
  const sev = SEVERITY_META[issue.severity];
  const isOpen = issue.status === 'open';

  async function resolve() {
    try {
      await update.mutateAsync({
        id: issue.id,
        body: { status: 'resolved', resolution_note: note.trim() || null },
      });
      toast.success('Issue resolved');
      setResolving(false);
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    }
  }

  async function reopen() {
    try {
      await update.mutateAsync({ id: issue.id, body: { status: 'open' } });
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    }
  }

  return (
    <li className={cn('rounded-lg border p-3', isOpen ? 'border-line' : 'border-line bg-fill-soft/40 opacity-80')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{issue.title}</span>
            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border capitalize', sev.cls)}>
              {sev.label}
            </span>
            {!isOpen && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-status-complete text-status-complete-fg">
                Resolved
              </span>
            )}
          </div>
          <div className="text-xs text-ink-faint mt-0.5">
            {issue.reported_by_name ?? 'Crew'} · {formatRelative(issue.created_at)}
          </div>
          {issue.description && (
            <p className="text-sm text-ink-dim mt-1 whitespace-pre-wrap">{issue.description}</p>
          )}
          {issue.photos.length > 0 && (
            <div className="mt-2">
              <MediaGallery media={issue.photos.map(toMedia)} size="sm" />
            </div>
          )}
          {!isOpen && issue.resolution_note && (
            <p className="text-xs text-ink-dim mt-2">
              <span className="text-ink-faint">Resolution:</span> {issue.resolution_note}
              {issue.resolved_by_name && ` — ${issue.resolved_by_name}`}
            </p>
          )}
        </div>
        <div className="shrink-0">
          {isOpen ? (
            !resolving && (
              <Button variant="outline" size="sm" onClick={() => setResolving(true)}>
                <Check className="w-3.5 h-3.5" /> Resolve
              </Button>
            )
          ) : (
            <Button variant="ghost" size="sm" onClick={() => void reopen()} disabled={update.isPending}>
              Reopen
            </Button>
          )}
        </div>
      </div>
      {resolving && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="How was it fixed? (optional)"
            className="h-8 text-sm"
          />
          <Button variant="ghost" size="icon" onClick={() => setResolving(false)} aria-label="Cancel">
            <X className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={() => void resolve()} disabled={update.isPending}>
            {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Done
          </Button>
        </div>
      )}
    </li>
  );
}
